import React, { useState, useEffect, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import mapValues from 'lodash/mapValues';
import ceil from 'lodash/ceil';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';

import NotFound from 'Components/NotFound/NotFound';
import {
  fetchPickAndMix,
  fetchPickAndMixPreview,
} from 'Redux/ducks/pickandmix';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import Loading from 'Components/Utility/Loading';
import { history } from 'Redux/store';
import { currencyCode } from 'Helpers/Geo/data';
import PickAndMixProductPage from 'Components/PickAndMix/PickAndMixProductPage';
import { combineProductRegions } from 'Components/PickAndMix/PickAndMixHelper';

const BundlePage = lazy(() => import(/* webpackChunkName: "Bundle" */ 'Components/Bundle/BundlePage'));

const PickAndMixContainer = ({
  match: { params: { slug, id } },
  siteTheme,
  suggestionsPreference,
  cartSlugs,
}) => {
  const [pickAndMix, setPickAndMix] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [redirect, setRedirect] = useState(null);

  const push = history.push;
  const preselected = getUrlParameter('preselected');
  const preselectedSlugs = preselected && preselected.split(',');

  useEffect(() => {
    setPickAndMix(null);
    setNotFound(null);
    setRedirect(null);

    const handleResult = (result) => {
      if (result.notFound) {
        return setNotFound(true);
      }
      if (result.redirect) {
        return setRedirect(result.redirect);
      }
      return setPickAndMix(result);
    };

    const fetch = async () => {
      const result = await fetchPickAndMix(slug);
      handleResult(result);
    };

    const fetchPreview = async () => {
      const key = getUrlParameter('key') || '';

      const result = await fetchPickAndMixPreview(id, encodeURIComponent(key));
      result.isPreview = true;
      handleResult(result);
    };

    if (id) {
      fetchPreview();
    } else {
      fetch();
    }
  }, [id, slug]);

  if (notFound) {
    return (
      <NotFound topSellersFilter="bundle" />
    );
  }

  if (redirect) {
    return (
      <NotFound redirect={redirect} topSellersFilter="bundle" />
    );
  }

  if (!pickAndMix) {
    return <Loading />;
  }

  const pnmProducts = get(pickAndMix, 'products', []);
  const preselectedProducts = (pnmProducts || []).filter(p => preselectedSlugs.includes(p.slug));
  const tiers = get(pickAndMix, 'tiers');
  const quantities = tiers?.map(tier => tier.quantity) || [];
  const preSelectedProductsLength = get(preselectedProducts, 'length');
  const tierMatch = quantities.includes(preSelectedProductsLength);


  if (preselected && tierMatch) {
    const quantity = preselectedProducts.length;
    let fullPrice = 0;

    preselectedProducts.forEach((p) => {
      fullPrice += p.price[currencyCode];
      p.display = true; // eslint-disable-line no-param-reassign
    });

    if (!quantity) {
      return [];
    }

    tiers.forEach((t) => {
      t.baseQuantity = t.quantity; // eslint-disable-line no-param-reassign
      t.basePrice = { ...t.price }; // eslint-disable-line no-param-reassign
    });

    const highestActiveTier = orderBy(tiers, 'quantity', 'desc').find(t => quantity >= t.quantity);

    if (!highestActiveTier) {
      return [];
    }

    const adjustedTier = {
      ...highestActiveTier,
      quantity,
      price: mapValues(
        highestActiveTier.basePrice,
        v => ceil((v / highestActiveTier.baseQuantity) * quantity),
      ),
    };

    const platforms = preselectedProducts.map(pp => pp.platforms);
    let validPlatforms = {};

    if (platforms && platforms.length > 0) {
      const initialObj = { windows: true, mac: true, linux: true };
      const reducedResult = reduce(platforms, (acc, obj) =>
        mapValues(acc, (value, key) => value && obj[key]), initialObj);

      validPlatforms = mapValues(reducedResult, Boolean);
    }

    const { regionsExcluded, regionsIncluded } = combineProductRegions(preselectedProducts);


    const product = {
      name: pickAndMix.name,
      type: 'bundle',
      drm: pickAndMix.drm,
      cover: pickAndMix.cover_image,
      template_type: 'standard',
      tiered: false,
      regions_excluded: regionsExcluded,
      regions_included: regionsIncluded,
      seo: {
        desc: pickAndMix.description,
      },
      price: adjustedTier.price,
      fullPrice: { [currencyCode]: fullPrice },
      desc: pickAndMix.about,
      platforms: validPlatforms,
      currentPrice: adjustedTier.price,
      img: [],
      bundles: [{
        displayTotal: preselectedProducts.length,
        downloadTotal: 0,
        filesTotal: 0,
        fullPrice: { [currencyCode]: fullPrice },
        games: preselectedProducts,
        incrementalFullPrice: fullPrice,
        price: adjustedTier.price,
      }],
    };
    return (
      <Suspense>
        <BundlePage
          preSelectedPnm
          product={product}
          push={push}
          selectTier={1}
        />
      </Suspense>
    );
  }

  const selectedProducts = pnmProducts.filter(p => cartSlugs.includes(p.slug));

  const isPreview = pickAndMix?.isPreview;
  return (
    <PickAndMixProductPage
      pickAndMix={pickAndMix}
      tiers={pickAndMix.tiers}
      siteTheme={siteTheme}
      suggestionsPreference={suggestionsPreference}
      selectedProducts={selectedProducts}
      isPreview={isPreview}
    />
  );
};

PickAndMixContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string,
      id: PropTypes.string,
    }),
  }).isRequired,
  siteTheme: PropTypes.string,
  suggestionsPreference: PropTypes.bool,
  cartSlugs: PropTypes.arrayOf(PropTypes.string),
};

PickAndMixContainer.defaultProps = {
  siteTheme: '',
  suggestionsPreference: true,
  cartSlugs: [],
};

const mapStateToProps = ({
  siteTheme,
  cart: { allSlugs: cartSlugs },
}) => ({
  siteTheme,
  cartSlugs,
});

export default connect(mapStateToProps)(PickAndMixContainer);
